import { Dialog, DialogContent, DialogTitle, Fab, FormControl, Grid, TextareaAutosize, Theme, Typography, TableContainer, Table, Paper, TableHead, TableRow, TableCell, TableBody } from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import { createStyles, makeStyles } from "@mui/styles";
import React, { useEffect, useState } from 'react'
import { v4 as uuidv4 } from "uuid";
import FlatPickerBar from "../../../../common/FlatPicker";
import { IPartner, IProcurementRequestItem, IPurchaseOrder, ProcurementItemStatuses } from '../../../../../reducers/Procurement/types'
import { DateTime as d } from "luxon"
import { NuvvenDivider } from '../../../ReservationManagement/Summary/Components/Divider'
import { useSelector } from 'react-redux'
import { IAppState } from '../../../../../store'
import { GET_PARTNERS_LIST } from '../../../../../graphql/Procurement/getPartnersListQuery'
import { useLazyQuery } from '@apollo/client'
import { DocumentDialog } from '../../../CustomerManagement/CustomerDetails/DocumentDialog'
import { useSnackBar } from '../../../../common/SnackBarContext/SnackBarContext'
import { DATE_TYPE, checkUploadFileFormat, uploadFileExtensionAndContentType } from '../../../../common/utils';
import { SnackBarVariant } from '../../../../common/SnackbarWrapper/SnackbarWrapper';
import { getLocalizedDateFormat } from '../../../../../utils/localized.syntex';
import { captureErrorException } from '../../../../../utils/sentry';

export const purchaseOrderInitials: IPurchaseOrder = {
  date: "",
  items: [],
  note: {
    createdAt: "",
    createdBy: "",
    description: ""
  },
  partnerId: "",
  validUntilDate: "",
  lastEmailSent: "",
  purchaseOrderId: "",
  quoteId: ""
}

interface IProps {
  purchaseOrder?: IPurchaseOrder;
  open: boolean;
  onClose(): void;
  handelFormSubmit(purchaseOrder: IPurchaseOrder): void;
  items: IProcurementRequestItem[];
  procurementExpectedDate: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(1),
      background: "#dee2e6"
    },
    formPaper: {
      padding: theme.spacing(3)
    },
    form: {
      flexGrow: 1
    }
  })
);

export const NewPurchaseOrderModel: React.FC<IProps> = (props) => {

  const { handelFormSubmit, procurementExpectedDate } = props;
  const classes = useStyles();
  const snackbar = useSnackBar();

  const { _e_ } = useSelector((state: IAppState) => state.authReducer);
  const userReducer = useSelector((state: IAppState) => state.userReducer);
  const { country } = userReducer.currentOrganisation.address

  const [values, setValues] = useState<IPurchaseOrder>(purchaseOrderInitials)
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [partners, setPartners] = useState<IPartner[]>([]);
  const [partner, setPartner] = useState<IPartner>()
  const [documentDialogVisible, setDocumentDialogVisible] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [itemIndex, setItemIndex] = useState<number>(-1)
  const [items, setItems] = useState<IProcurementRequestItem[]>([])

  const [
    loadPartners,
    { data }
  ] = useLazyQuery(GET_PARTNERS_LIST, { fetchPolicy: "network-only" });

  useEffect(() => {
    if (userReducer.tenancy) {
      loadPartners()
    }
  }, [userReducer.tenancy])

  useEffect(() => {
    if (data && data.partners) {
      setPartners(data.partners)
      const partner = data.partners.find((partner: IPartner) => {
        return partner.id === values.partnerId
      })
      setPartner(partner)
    }
  }, [data])

  useEffect(() => {
    if (props.items) {
      const products = props.items.filter((item) => (item.status === ProcurementItemStatuses.APPROVED))
      setItems(products)
    }
  }, [props.items])

  useEffect(() => {
    if (props.purchaseOrder?.id) {
      setIsUpdate(true)
      setValues(props.purchaseOrder)
    } else {
      setValues(purchaseOrderInitials)
    }
  }, [props.purchaseOrder])

  async function uploadDocument(
    file: File
  ) {
    try {
      if (!userReducer.tenancy?.id) {
        return;
      }
      // 5MB limit exceeded
      if (file.size > 5000000) {
        throw new Error("File size exceeded limit of 5MB");
      }
      setUploadInProgress(true);
      if (checkUploadFileFormat(file.type)) {
        const {
          fileExtension
        } = uploadFileExtensionAndContentType(file.type);
        const uniqueId = uuidv4();
        const key = `${userReducer.tenancy.id}/${uniqueId}.${fileExtension}`;
        if (_e_) {
          await _e_
            .add({
              name: key,
              file: file,
              complete: async () => {
                if (itemIndex >= 0) {
                  values.items[itemIndex].documents = [...values.items[itemIndex].documents, key]
                }
                setValues(values)
                snackbar({
                  message: "Document Added",
                  variant: SnackBarVariant.SUCCESS
                });
                setUploadInProgress(false);
                setDocumentDialogVisible(false);
              }
            });
        }
      } else {
        setDocumentDialogVisible(false);
        setUploadInProgress(false);
        return snackbar({
          message: "Please only upload .pdf!",
          variant: SnackBarVariant.ERROR
        });
      }
    } catch (err: any) {
      captureErrorException(err)
      snackbar({ message: err?.message, variant: SnackBarVariant.ERROR });
    }
  }

  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => {
          props.onClose()
        }}
        maxWidth={"lg"}
      >
        <DialogTitle id="form-dialog-title">
          <Grid container item xs={12}>
            <Typography variant="h2">{isUpdate ? "Update Purchase Order" : "New Purchase Order"}</Typography>
          </Grid>
          <NuvvenDivider noMargin />
        </DialogTitle>
        <DialogContent>
          <Grid item xs={12}>
            <Formik
              enableReinitialize
              initialValues={values}
              onSubmit={(values, { setSubmitting }) => {
                if (values.date && values.validUntilDate) {
                  handelFormSubmit(values)
                  setSubmitting(false)
                } else {
                  return snackbar({
                    message: "Please select the required dates.",
                    variant: SnackBarVariant.ERROR
                  });
                }
              }}
            >
              {(props) => (
                <Form className={classes.form}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant='h3'>PO Information</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        component={TextField}
                        placeholder="e.g TEST"
                        label="PO ID"
                        name={"purchaseOrderId"}
                        inputProps={{
                          onChange: (event: any) => {
                            setValues({
                              ...props.values,
                              purchaseOrderId: event.target.value
                            })
                          },
                          value: props.values.purchaseOrderId
                        }}
                        onChange={props.handleChange}
                        fullWidth
                        required
                        disabled={props.values.purchaseOrderId ? true : false}
                      ></Field>
                    </Grid>
                    <Grid item xs={3}>
                      <FlatPickerBar
                        enableTime={false}
                        handleDateChange={(value: Date) => {
                          setValues({
                            ...props.values,
                            date: d.fromJSDate(value).toUTC().toISO()
                          })
                        }}
                        label={"PO Date"}
                        identifier={"date"}
                        value={getLocalizedDateFormat(country, props.values.date, DATE_TYPE.CONDENSED)}
                        minDate={getLocalizedDateFormat(country, procurementExpectedDate, DATE_TYPE.CONDENSED)}
                        required
                        country={country}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Field
                        component={TextField}
                        placeholder="e.g TEST"
                        label="Partner"
                        name={"partnerName"}
                        value={props.values.partner?.partnerName}
                        onChange={props.handleChange}
                        fullWidth
                        required
                        country={country}
                        disabled
                      ></Field>
                    </Grid>
                    <Grid item xs={3}>
                      <FlatPickerBar
                        enableTime={false}
                        handleDateChange={(value: Date) => {
                          setValues({
                            ...props.values,
                            validUntilDate: d.fromJSDate(value).toUTC().toISO()
                          })
                        }}
                        label={"Valid Untill"}
                        identifier={"validUntilDate"}
                        value={getLocalizedDateFormat(country, props.values.validUntilDate, DATE_TYPE.CONDENSED)}
                        minDate={getLocalizedDateFormat(country, props.values.date, DATE_TYPE.CONDENSED)}
                        required
                        country={country}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                          <TableHead>
                            <TableRow>
                              <TableCell className={"tableCellHead"}>No. of Vehicles</TableCell>
                              <TableCell className={"tableCellHead"} align="left">Make</TableCell>
                              <TableCell className={"tableCellHead"} align="left">Model</TableCell>
                              <TableCell className={"tableCellHead"} align="left">Variant</TableCell>
                              <TableCell className={"tableCellHead"} align="left">Fuel</TableCell>
                              <TableCell className={"tableCellHead"} align="left">Transmission</TableCell>
                              <TableCell className={"tableCellHead"} align="left">Condition</TableCell>
                              <TableCell className={"tableCellHead"} align="left">Expected by</TableCell>
                              <TableCell className={"tableCellHead"} align="left">Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {
                              items &&
                              items.map((row: IProcurementRequestItem, index: number) => (
                                <TableRow
                                  key={row.requestId}
                                >
                                  <TableCell align="left">{row.quantity}</TableCell>
                                  <TableCell align="left">{row.make}</TableCell>
                                  <TableCell align="left">{row.model}</TableCell>
                                  <TableCell align="left">{row.variant}</TableCell>
                                  <TableCell align="left">{row.fuel}</TableCell>
                                  <TableCell align="left">{row.transmission}</TableCell>
                                  <TableCell align="left">{row.condition}</TableCell>
                                  <TableCell align="left">{d.fromISO(row.expectedByDate).toFormat('dd/MM/yyyy')}</TableCell>
                                  <TableCell align="left"></TableCell>
                                </TableRow>
                              ))
                            }
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={12}><Typography variant={"h3"}>Add Notes</Typography></Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <TextareaAutosize
                          maxRows={10}
                          minRows={6}
                          placeholder="add your note here..."
                          value={props.values.note.description}
                          onChange={(e: any) => {
                            setValues({
                              ...props.values,
                              note: {
                                ...props.values.note,
                                description: e.target.value,
                                createdBy: userReducer.id || "",
                                createdAt: d.now().toUTC().toISO()
                              }
                            })
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item container xs={12} justifyContent="flex-start">
                      <Fab
                        variant="extended"
                        size="medium"
                        aria-label="add"
                        type="submit"
                      >
                        Save
                      </Fab>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        </DialogContent>
        <DocumentDialog
          open={documentDialogVisible}
          handleClose={() => setDocumentDialogVisible(false)}
          uploadInProgress={uploadInProgress}
          onSubmit={uploadDocument}
          accept="application/zip"
        />
      </Dialog>
    </>
  )
}
